'use client';

import { c as _c } from "react/compiler-runtime";
import avatarIcon from '@public/images/avatar.svg';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@src/components/Button/Button';
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink';
import { Stamp } from '@src/components/TopNavV5/Stamp';
import { useTopNavV5 } from '@src/components/TopNavV5/TopNavV5Context';
import { TopNavV5DesktopMiniMenu } from '@src/components/TopNavV5/TopNavV5Desktop/TopNavV5DesktopMiniMenu';
import type { TopNavV5MenuItem } from '@src/components/TopNavV5/TopNavV5Props';
import { configPublic } from '@src/config.public';
import { useSession } from '@src/hooks/useSession';
import { useLocale } from '@src/i18n/LocaleProvider';
import { useTrackSelectNavCta } from '@src/utils/analytics.util';
import { cn } from '@src/utils/tailwind';
const itemClasses = 'flex items-center cursor-pointer h-full transition-all hover:text-bodi-white text-body-md! text-nowrap';
const hoverTimeouts: Record<string, number> = {};
const hoverDelay = configPublic.general.topNavDelay;
export const TopNavV5DesktopBar = () => {
  const $ = _c(48);
  const {
    t
  } = useLocale("common");
  const {
    user,
    getLoginHref
  } = useSession();
  let t0;
  if ($[0] !== getLoginHref) {
    t0 = getLoginHref();
    $[0] = getLoginHref;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const loginHref = t0;
  const [accountContainerWidth, setAccountContainerWidth] = useState(getInitialContainerWidth());
  const trackSelectNavCta = useTrackSelectNavCta();
  const {
    logo,
    items,
    account,
    isExpanded,
    expandedMenu,
    setExpandedMenu,
    setExpanded,
    setAccountExpanded,
    topNavDesktopHeight
  } = useTopNavV5();
  const accountContainerRef = useRef(null);
  function getInitialContainerWidth() {
    if (!user) {
      return 302;
    }
    if (user?.hasBodiAccess) {
      return 157;
    }
    if (!user?.hasBodiAccess) {
      return 300;
    }
  }
  let t1;
  if ($[2] !== setAccountContainerWidth) {
    t1 = () => {
      const updateWidth = function updateWidth() {
        if (accountContainerRef.current) {
          setAccountContainerWidth(accountContainerRef.current.offsetWidth);
        }
      };
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    };
    $[2] = setAccountContainerWidth;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== expandedMenu?.title || $[6] !== isExpanded || $[7] !== setExpanded || $[8] !== setExpandedMenu || $[9] !== trackSelectNavCta) {
    t3 = items_0 => items_0?.filter(_temp).map((item_0, index) => {
      const isSelected = item_0.title === expandedMenu?.title && isExpanded;
      const componentKey = `${item_0.title}-${index}`;
      const isLink = item_0.subMenuType === "link";
      const isMiniMenu = item_0.subMenuType === "miniMenu";
      if (isLink) {
        const {
          href,
          newTab
        } = item_0.link ?? {};
        return <LocalizedLink data-testid={`nav-item-${index}`} onMouseEnter={() => setExpanded(false)} onClick={() => {
          trackSelectNavCta({
            "cta url": href
          });
        }} key={componentKey} className={cn(itemClasses, "group relative")} href={href ?? ""} rel={newTab ? "noopener noreferrer" : ""} target={newTab ? "_blank" : "_self"}>{item_0.title}<Stamp text={item_0.stamp} /><div className="absolute bottom-0 left-0 h-[3px] w-full bg-bodi-accent-bright-500 opacity-0 transition-all duration-200 group-hover:opacity-100" /></LocalizedLink>;
      }
      if (isMiniMenu) {
        return <TopNavV5DesktopMiniMenu key={componentKey} componentKey={componentKey} item={item_0} index={index} itemClasses={itemClasses} />;
      }
      return <button data-testid={`nav-item-${index}`} key={componentKey} onMouseEnter={() => {
        setExpandedMenu(item_0);
        hoverTimeouts[componentKey] = window.setTimeout(() => setExpanded(true), hoverDelay);
      }} onMouseLeave={() => {
        clearTimeout(hoverTimeouts[componentKey]);
      }} onClick={() => {
        clearTimeout(hoverTimeouts[componentKey]);
        setExpanded(!isExpanded);
      }} className={cn(itemClasses, "relative flex appearance-none items-center gap-2 font-normal", {
        "text-bodi-white font-medium": isSelected
      })}>{item_0.title}<Stamp text={item_0.stamp} /><div className={cn("absolute bottom-0 left-0 h-[3px] w-full bg-bodi-accent-bright-500 transition-opacity", {
          "opacity-100": isSelected,
          "opacity-0": !isSelected
        })} /></button>;
    });
    $[5] = expandedMenu?.title;
    $[6] = isExpanded;
    $[7] = setExpanded;
    $[8] = setExpandedMenu;
    $[9] = trackSelectNavCta;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const renderItems = t3;
  const t4 = `${topNavDesktopHeight}px`;
  let t5;
  if ($[11] !== t4) {
    t5 = {
      height: t4
    };
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== accountContainerWidth || $[14] !== logo || $[15] !== setExpanded || $[16] !== trackSelectNavCta) {
    t6 = logo?.src && <div className="flex h-full items-center" style={accountContainerWidth ? {
      width: accountContainerWidth
    } : undefined}><LocalizedLink onMouseEnter={() => setExpanded(false)} onClick={() => {
        trackSelectNavCta({
          "cta url": logo?.href
        });
      }} href={logo?.href ?? ""} className="flex h-full shrink-0 items-center"><Image src={logo.src} width={logo?.desktopWidth} height={logo?.desktopHeight} alt={logo?.altText ?? ""} /></LocalizedLink></div>;
    $[13] = accountContainerWidth;
    $[14] = logo;
    $[15] = setExpanded;
    $[16] = trackSelectNavCta;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== items || $[19] !== renderItems) {
    t7 = renderItems(items);
    $[18] = items;
    $[19] = renderItems;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t7) {
    t8 = <div className="flex h-full items-center gap-6 px-4">{t7}</div>;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== setExpanded) {
    t9 = () => setExpanded(false);
    $[23] = setExpanded;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== loginHref || $[26] !== setAccountExpanded || $[27] !== t || $[28] !== trackSelectNavCta || $[29] !== user) {
    t10 = !user ? <a href={loginHref}><Button size="small" variant="black" className="border border-solid border-bodi-gray-medium" onClick={() => {
        trackSelectNavCta({
          "cta url": loginHref
        });
      }} data-testid="loginBtn">{t("login")}</Button></a> : <button onClick={() => {
      setAccountExpanded(true);
    }} data-testid="accountBtn" className="flex items-center gap-2 font-medium text-bodi-gray-butters"><Image src={avatarIcon} width={24} height={24} alt="Account icon" />{t("my-account")}</button>;
    $[25] = loginHref;
    $[26] = setAccountExpanded;
    $[27] = t;
    $[28] = trackSelectNavCta;
    $[29] = user;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== account?.bodiCta?.href || $[32] !== account?.bodiCta?.newTab || $[33] !== account?.bodiCta?.title || $[34] !== trackSelectNavCta || $[35] !== user?.hasBodiAccess) {
    t11 = !user?.hasBodiAccess && account?.bodiCta?.title && account?.bodiCta?.href && <LocalizedLink onClick={() => {
      trackSelectNavCta({
        "cta url": account?.bodiCta?.href
      });
    }} data-testid="bodiBtn" href={account?.bodiCta?.href ?? ""} rel={account?.bodiCta?.newTab ? "noopener noreferrer" : ""} target={account?.bodiCta?.newTab ? "_blank" : "_self"}><Button className="[&>div]:px-8" size="small">{account?.bodiCta?.title}</Button></LocalizedLink>;
    $[31] = account?.bodiCta?.href;
    $[32] = account?.bodiCta?.newTab;
    $[33] = account?.bodiCta?.title;
    $[34] = trackSelectNavCta;
    $[35] = user?.hasBodiAccess;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t9) {
    t12 = <div onMouseEnter={t9} className="flex h-full shrink-0 items-center justify-end gap-2" ref={accountContainerRef}>{t10}{t11}</div>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t9;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t12 || $[42] !== t6 || $[43] !== t8) {
    t13 = <div className="flex h-full items-center justify-between">{t6}{t8}{t12}</div>;
    $[41] = t12;
    $[42] = t6;
    $[43] = t8;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t13 || $[46] !== t5) {
    t14 = <div style={t5} className="relative z-50 w-full bg-bodi-black-text px-4 font-medium text-bodi-gray-butters">{t13}</div>;
    $[45] = t13;
    $[46] = t5;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
};
function _temp(item) {
  return Boolean(item.title);
}